<template>
  <div class="-categories-manager wt2-sg">
    <i
      v-if="showCloseIcon"
      class="wt2-close wticon wticon-close wt2-color-normal wt2-text-m wt2-text-lm close-icon"
      @click="$emit('close')"
    />

    <div v-if="isLoading" class="wt-loader -categories-loader" />
    <div v-else-if="fetchError" class="-fetch-error">
      Couldn't fetch categories.
      <br />Please try again or
      <a @click="contactSupport(0)" class="wt-cursor-pointer">Contact Support</a
      ><br />
    </div>
    <div v-else class="-categories-manager-inner">
      <div class="-header">
        <div class="-title wt2-color-primary wt2-text-m2">Manage Topics</div>
        <div class="-subtitle">Add, Rename or Delete</div>
      </div>
      <div class="-add-new-container">
        <wt-button
          class="-default -primary -add-new-btn"
          v-if="!showAddNewCategory && !isLoading"
          title="Add new topic"
          @click="displayAddNewCategory"
          text="Add New"
        />
        <div class="-add-new-block" :class="{ '-active': showAddNewCategory }">
          <div class="-add-new">
            <div class="-add-new-inner">
              <div class="-add-new-tmp-category">
                <input
                  v-model="newCategoryName"
                  :disabled="addingNewCategory"
                  ref="newCategoryName"
                  type="text"
                  class="wt2-input -new-category-name"
                  placeholder="Type a name for your new topic"
                  @keyup.enter="addNew"
                  v-click-outside="onClickOutsideNewCategory"
                />
                <wt-google-like-loader
                  v-show="addingNewCategory"
                  class="-default -spinner"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- <div
        class="-add-new-btn"
        title="Add new topic"
        @click="displayAddNewCategory"
        v-if="!showAddNewCategory && !isLoading"
      >
        <i class="wticon wticon-plus" />
      </div> -->
      <div class="-categories" ref="scrollable">
        <div
          v-for="category in categories"
          :key="category.id"
          class="-category"
          :class="{
            '-working':
              deletingCategories.includes(category.id) ||
              renamingCategories.includes(category.id),
            '-editing': category.id === categoryInEditId,
          }"
        >
          <div class="-category-inner">
            <div
              class="-category-name js-categories-manager-category-name"
              :data-id="category.id"
              :class="{ '-editing': category.id === categoryInEditId }"
              title="Click to rename"
            >
              <input
                v-if="category.id === categoryInEditId"
                class="js-categories-manager-category-name"
                :data-id="category.id"
                type="text"
                :value="category.categoryName"
                @focus="onFocusCategoryName"
                @blur="renameCategory"
                @keyup.enter="renameCategory"
                ref="editCategoryInput"
              />
              <span
                v-else
                v-text="`${category.categoryName}`"
                class="js-categories-manager-category-name"
                :data-id="category.id"
              />
            </div>
            <div class="-number-of-templates" title="Number of templates">
              {{ category.numberOfTemplates }}
            </div>
            <wt-google-like-loader
              v-show="
                deletingCategories.includes(category.id) ||
                renamingCategories.includes(category.id)
              "
              class="-default -spinner"
            />
            <div class="-category-actions">
              <div
                class="-category-action -category-action-delete"
                title="Remove topic"
                @click="deleteTopic(category.id)"
              >
                <i class="wticon wticon-trash2" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import app from 'app';
import Vue from 'vue';
import { UNCATEGORIZED_FLAG } from '#/const';

export default Vue.extend({
  name: 'CategoriesManager',
  store: app.rootStore,
  props: {
    showCloseIcon: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isLoading: true,
      fetchError: false,
      categories: [],
      deletingCategories: [],
      renamingCategories: [],
      categoryInEditId: null,
      newCategoryName: '',
      showAddNewCategory: false,
      addingNewCategory: false,
      renamingCategoryElement: null,
    };
  },
  created() {
    app.api.storyboards
      .getAllCategories()
      .then(({ channelOwnedCategories }) => {
        this.categories = channelOwnedCategories.filter(
          (category) => category.id !== UNCATEGORIZED_FLAG
        );
        this.isLoading = false;
      })
      .catch((error) => {
        this.fetchError = true;
        console.error(`CategoriesManager -> Couldn't fetch categories`, error);
      });
  },
  mounted() {
    document.addEventListener('click', this.onClickElement);
  },
  beforeDestroy() {
    app.keyboardAccessibility.remove('wt-categories-manager-add-new');
    app.keyboardAccessibility.remove('wt-categories-manager-rename-input');
    document.removeEventListener('click', this.onClickElement);
  },
  methods: {
    onClickOutsideNewCategory() {
      if (this.showAddNewCategory) {
        if (!this.newCategoryName.trim().length) {
          this.discardAddNewCategory();
        } else {
          this.addNew();
        }
      }
    },
    displayAddNewCategory() {
      this.showAddNewCategory = true;
      this.$nextTick(() => {
        this.newCategoryName = '';
        this.$refs.newCategoryName.focus();
      });
      app.keyboardAccessibility.add('wt-categories-manager-add-new', [
        {
          on: 'keydown',
          key: $.ui.keyCode.ESCAPE,
          inEditor: true,
          run: this.discardAddNewCategory,
        },
      ]);
    },
    discardAddNewCategory() {
      this.newCategoryName = '';
      this.showAddNewCategory = false;
      app.keyboardAccessibility.remove('wt-categories-manager-add-new');
    },
    onFocusCategoryName(e) {
      app.keyboardAccessibility.add('wt-categories-manager-rename-input', [
        {
          on: 'keydown',
          key: $.ui.keyCode.ESCAPE,
          inEditor: true,
          run: this.discardRenameCategory,
        },
      ]);
    },
    discardRenameCategory() {
      if (this.$refs.editCategoryInput && this.$refs.editCategoryInput[0]) {
        const categoryId = this.$refs.editCategoryInput[0].dataset.id;
        const currentCategory = this.categories.find(
          (category) => category.id === categoryId
        );
        this.$refs.editCategoryInput[0].value = currentCategory.categoryName;
        this.$refs.editCategoryInput[0].blur();
      }
      this.categoryInEditId = null;
      app.keyboardAccessibility.remove('wt-categories-manager-rename-input');
    },
    renameCategory(e) {
      app.keyboardAccessibility.remove('wt-categories-manager-rename-input');
      const categoryName = e.srcElement.value.replace(/(\r\n|\n|\r)/gm, '');
      const categoryId = e.srcElement.dataset.id;
      const currentCategory = this.categories.find(
        (category) => category.id === categoryId
      );
      if (!currentCategory || currentCategory.categoryName === categoryName) {
        return;
      }
      const originalCategoryName = currentCategory.categoryName;
      if (this.categoryInEditId === categoryId) {
        this.categoryInEditId = null;
      }
      currentCategory.categoryName = categoryName;
      this.renamingCategories.push(categoryId);
      app.rootStore
        .dispatch('RENAME_STORYBOARD_CATEGORY', {
          categoryId,
          categoryName,
        })
        .then(() => {
          this.$emit('rename', { categoryId });
        })
        .catch((error) => {
          currentCategory.categoryName = originalCategoryName;
          if (error && error.errorCode === 409) {
            app.notificationController.push({
              text: `A topic with the name "${categoryName}" already exists`,
              type: 'WARN',
              timeout: app.TIME.second(3),
              uniqueGroupName: true,
            });
            if (!this.categoryInEditId) {
              this.categoryInEditId = categoryId;
              this.$nextTick(() => {
                this.$refs.editCategoryInput[0]?.focus();
              });
            }
          } else {
            app.notificationController.push({
              type: 'ERROR',
              text: `"${categoryName}" - Couldn't rename topic`,
              timeout: app.TIME.second(5),
            });
            console.error(
              `CategoriesManager -> Couldn't rename category`,
              error
            );
          }
        })
        .finally(() => {
          this.renamingCategories = this.renamingCategories.filter(
            (id) => id !== categoryId
          );
        });
    },
    onClickElement(e) {
      if (
        e.target &&
        e.target.classList.contains('js-categories-manager-category-name')
      ) {
        this.categoryInEditId = e.target.dataset.id;
        this.$nextTick(() => {
          this.$refs.editCategoryInput[0]?.focus();
        });
      } else {
        this.categoryInEditId = null;
      }
    },
    addNew() {
      const categoryName = String(this.newCategoryName).trim();
      this.newCategoryError = false;
      if (categoryName.length === 0) {
        return;
      }
      this.addingNewCategory = true;
      app.rootStore
        .dispatch('ADD_STORYBOARD_CATEGORY', categoryName)
        .then((category) => {
          this.categories.push(category);
          this.$nextTick(() => {
            const $scrollable = this.$refs.scrollable;
            if ($scrollable) {
              $scrollable.scrollTo(0, $scrollable.scrollHeight);
            }
          });
          this.$emit('new', { categoryName, id: category.id });
          this.newCategoryName = '';
          this.showAddNewCategory = false;
          app.keyboardAccessibility.remove('wt-categories-manager-add-new');
        })
        .catch((error) => {
          let errString = `Couldn't add new topic ("${categoryName}") - please try again or contact support`;
          let type = 'ERROR';
          if (error && error.errorCode === 409) {
            type = 'WARN';
            errString = `A topic with the name "${categoryName}" already exists`;
          } else {
            console.error(
              `CategoriesManager -> Couldn't add new category`,
              error
            );
          }
          app.notificationController.push({
            type,
            text: `${errString} `,
            timeout: app.TIME.second(5),
          });

          this.newCategoryError = true;
          this.$refs.newCategoryName.focus();
        })
        .finally(() => {
          this.addingNewCategory = false;
        });
    },
    deleteTopic(categoryId) {
      if (this.deletingCategories.includes(categoryId)) {
        return;
      }
      const category = this.categories.find(
        (category) => category.id === categoryId
      );
      if (!category) {
        return;
      }
      this.renamingCategories = this.renamingCategories.filter(
        (id) => id !== categoryId
      );

      const trimmedCategoryName =
        category.categoryName.length > 99
          ? category.categoryName.slice(0, 99) + '...'
          : category.categoryName;
      app.dialog
        .confirm({
          type: app.dialog.types.CONFIRM_3,
          title: 'Remove Topic',
          description: `All templates assigned to topic "${trimmedCategoryName}" will be impacted.<br/><br/>Are you sure you want to continue?`,
          buttonNoText: 'Cancel',
          buttonYesText: 'Remove',
        })
        .then((data) => {
          if (data.action) {
            this.deletingCategories.push(categoryId);
            app.rootStore
              .dispatch('DELETE_STORYBOARD_CATEGORY', categoryId)
              .then(() => {
                this.categories = this.categories.filter(
                  (category) => category.id !== categoryId
                );
                this.$emit('delete', categoryId);
              })
              .catch((error) => {
                app.notificationController.push({
                  type: 'ERROR',
                  text: `Couldn't delete topic "${category.categoryName}"`,
                  timeout: app.TIME.second(5),
                });
                console.error(
                  `CategoriesManager -> Couldn't delete category`,
                  error
                );
              })
              .finally(() => {
                this.deletingCategories = this.deletingCategories.filter(
                  (id) => id !== categoryId
                );
                const $modalEl = document.querySelector('#wtModal');
                if ($modalEl) {
                  $modalEl.style.zIndex = null;
                  $modalEl.style.position = null;
                }
              });
          }
        });

      // below is a hack to fix the z-index of the modal
      const $modalEl = document.querySelector('#wtModal');
      if ($modalEl) {
        $modalEl.style.zIndex = 9999999;
        $modalEl.style.position = 'fixed';
      }
    },
    contactSupport() {
      const message =
        "Hi. I'm having trouble with managing my topics, " + 'can you assist?';
      app.intercomController.showNewMessage(message).catch(() => {
        // fallback - if there no intercom, go to website support
        app.openSupportTab();
      });
    },
  },
});
</script>

<style lang="scss">
.-categories-manager {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  .close-icon {
    top: 2px !important;
    right: 20px;
    position: absolute !important;
  }
  .-categories-loader {
    transform: scale(0.25);
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) scale(0.25);
  }
  .-fetch-error {
    font-size: 14px;
    text-align: center !important;
  }
  .-categories-manager-inner {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;

    .-header {
      flex-shrink: 0;
      .-title {
        margin-bottom: 5px;
      }
      .-subtitle {
        margin-bottom: 15px !important;
        font-weight: 500;
        font-size: 14px;
      }
    }
  }

  .-categories {
    flex-grow: 1;
    overflow-y: auto;

    .-category {
      width: 100%;
      border-bottom: 1px solid rgba(89, 89, 89, 0.15);
      &.-editing {
        border-bottom: 1px solid rgba(89, 89, 89, 0.6);
      }
      &:hover:not(.-editing) {
        background: rgba(6, 169, 244, 0.05);
      }

      &.-working {
        color: rgba(0, 0, 0, 0.25);
        pointer-events: none;
      }
      .-category-inner {
        display: flex;
        align-items: center;
        min-height: 45px;
        &:hover {
          .-category-actions {
            display: flex;
          }
        }
        .-category-name {
          font-size: 14px;
          cursor: pointer;
          flex-grow: 1;
          height: 45px;
          display: flex;
          align-items: center;
          overflow: hidden;
          padding-right: 10px;
          span {
            padding-left: 5px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          input {
            flex-grow: 1;
            font-size: 14px !important;
            border: none !important;
            height: 100% !important;
            padding-left: 5px !important;
            &:hover,
            &:focus,
            &:active {
              background: rgba(0, 0, 0, 0.05);
            }
          }
        }
        .-spinner {
          margin-left: 10px;
        }
        .-category-actions {
          margin-right: 5px;
          .-category-action {
            margin-left: 10px;
            width: 25px;
            height: 25px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            transition: all 0.2s ease;
            font-size: 14px;
            &:hover {
              // background: #f2f2f2;
              color: #06a9f4;
            }
            &.-category-action-delete {
            }
          }
        }
      }
    }
  }

  .-add-new-container {
    margin-bottom: 10px;
    min-height: 35px;
    display: flex;
    align-items: center;

    button.primary.-add-new-btn {
      padding: 5px;
      font-size: 12px;
      min-width: 80px;
      height: 29px;
    }

    .-add-new-block {
      display: flex;
      flex-shrink: 0;
      align-items: center;
      height: 100%;
      width: 100%;
      display: none;
      &.-active {
        display: flex;
      }
      .-add-new {
        width: 100%;

        .-add-new-inner {
          display: flex;
          align-items: center;
          width: 100%;
          background: white;
        }

        .-add-new-tmp-category {
          width: 100%;
          position: relative;
          .-new-category-name {
            width: 100%;
            @include showPlaceHolder();
          }
          .-spinner {
            position: absolute;
            right: 10px;
            bottom: 10px;
          }
        }
      }
    }
  }
}
</style>
